/**
 * 잔여 PoC 도메인 — erp_poc_row JSON persistence
 */
import { isBdApiEnabled } from 'config/bdApiConfig';
import {
  cacheGet,
  cacheSet,
  commonSaveList,
  commonSelect,
  diffRowsByUid,
  parseJsonObject,
} from 'services/commonQuery';

export const POC_SAVE_QUERY = ['ErpPoc.insertRow', 'ErpPoc.updateRow', 'ErpPoc.deleteRow'];

export const POC_BUCKET = {
  INCOME_SOURCE: 'income.source',
  INCOME_RESOLUTION: 'income.resolution',
  INCOME_RES_VENDOR: 'income.resolutionVendor',
  INCOME_RECEIPT: 'income.receipt',
  INCOME_ADJUST: 'income.adjust',
  INCOME_OVERPAY: 'income.overpay',
  INCOME_WRITEOFF: 'income.writeoff',
  CONTRACT: 'contract.master',
  CONTRACT_OBL: 'contract.oblLink',
  CONTRACT_EXT: 'contract.extRef',
  MONTHLY: 'budget.monthly',
  EVIDENCE: 'expense.evidence',
  CARD_AXIS: 'expense.cardAxis',
  INCOME_EARNER: 'expense.incomeEarner',
  INCOME_STMT: 'expense.incomeStatement',
  TYPE1_DRAFT: 'expense.type1Draft',
  FORM_CATALOG: 'cm.formCatalog',
  FORM_MATCH: 'cm.formMatch',
  FORM_ACL: 'cm.formAcl',
  FORM_BIND: 'cm.formBind',
  REPORT_CATALOG: 'cm.reportCatalog',
  REPORT_BIND: 'cm.reportBind',
  SUBJECT_HIST: 'bd.subjectHistory',
  PROJECT_HIST: 'bd.projectHistory',
};

export const FILE_BUCKET = {
  FORM_TEMPLATE: 'form.template',
  RESOLUTION_FORM: 'resolution.form',
  CONTRACT_REQUEST: 'contract.request',
};

function stripMod(row = {}) {
  const { mod: _m, ...rest } = row;
  return rest;
}

export function mapPocPayloadRows(apiRows, uidKey) {
  return (apiRows || []).map((r) => {
    const p = parseJsonObject(r.payloadJson);
    const uid = r.rowUid || p[uidKey];
    return { ...p, [uidKey]: uid, mod: '' };
  });
}

const persistQueues = new Map();

export function enqueuePersist(key, fn) {
  const prev = persistQueues.get(key) || Promise.resolve();
  const next = prev.then(fn, fn);
  persistQueues.set(key, next);
  return next;
}

export function pocParams(dims = {}, extra = {}) {
  const params = {
    tenantCode: String(dims.tenantCode || '').trim() || undefined,
    ...extra,
  };
  if (dims.fiscalYear != null && dims.fiscalYear !== '') {
    params.fiscalYear = Number(dims.fiscalYear) || dims.fiscalYear;
  }
  return params;
}

export async function loadPocRows({ bucket, params = {}, cache, cacheKey, uidKey, globalYn }) {
  const rows = await commonSelect('ErpPoc.selectRowList', {
    bucket,
    globalYn: globalYn ? 'Y' : undefined,
    ...params,
  });
  const mapped = mapPocPayloadRows(rows, uidKey);
  if (cache && cacheKey != null) cacheSet(cache, cacheKey, mapped);
  return mapped;
}

export async function savePocRows({
  bucket,
  params = {},
  cache,
  cacheKey,
  uidKey,
  nextRows,
  ensureUid,
  prevRows,
  globalYn,
  parentUidOf,
  userKey,
}) {
  const prev =
    prevRows ||
    (cache && cacheKey != null ? cacheGet(cache, cacheKey) : null) ||
    (await loadPocRows({ bucket, params, cache, cacheKey, uidKey, globalYn }));
  const addValue = {
    ...params,
    bucket,
    globalYn: globalYn ? 'Y' : undefined,
    userKey: userKey || params.userKey || undefined,
  };
  const dirty = diffRowsByUid(prev, nextRows, uidKey, ensureUid).map((r) => {
    if (r.mod === 'D') {
      return { rowUid: r[uidKey], bucket, mod: 'D' };
    }
    const payload = stripMod(r);
    return {
      rowUid: r[uidKey],
      bucket,
      parentUid: parentUidOf ? parentUidOf(r) : r.parentUid || '',
      userKey: userKey || params.userKey || '',
      fiscalYear: params.fiscalYear,
      payloadJson: JSON.stringify(payload),
      globalYn: globalYn ? 'Y' : undefined,
      mod: r.mod,
    };
  });
  const del = dirty.filter((r) => r.mod === 'D');
  const ups = dirty.filter((r) => r.mod === 'A' || r.mod === 'U');
  if (del.length) await commonSaveList(POC_SAVE_QUERY, del, addValue);
  if (ups.length) await commonSaveList(POC_SAVE_QUERY, ups, addValue);
  return loadPocRows({ bucket, params, cache, cacheKey, uidKey, globalYn });
}

/** 단건 문서 (레지스트리·외관·직인·초안) */
export async function loadPocDoc({ bucket, rowUid, params = {}, globalYn }) {
  const rows = await commonSelect('ErpPoc.selectRowList', {
    bucket,
    rowUid,
    globalYn: globalYn ? 'Y' : undefined,
    ...params,
  });
  if (!rows?.length) return null;
  return parseJsonObject(rows[0].payloadJson);
}

export async function savePocDoc({
  bucket,
  rowUid,
  payload,
  params = {},
  globalYn,
  userKey,
  parentUid,
  exists,
}) {
  const addValue = {
    ...params,
    bucket,
    globalYn: globalYn ? 'Y' : undefined,
  };
  if (payload == null) {
    if (exists) {
      await commonSaveList(POC_SAVE_QUERY, [{ rowUid, bucket, mod: 'D' }], addValue);
    }
    return null;
  }
  const row = {
    rowUid,
    bucket,
    parentUid: parentUid || '',
    userKey: userKey || params.userKey || '',
    fiscalYear: params.fiscalYear,
    payloadJson: JSON.stringify(payload),
    globalYn: globalYn ? 'Y' : undefined,
    mod: exists ? 'U' : 'A',
  };
  await commonSaveList(POC_SAVE_QUERY, [row], addValue);
  return payload;
}

export async function upsertPocDoc(opts) {
  const existing = await loadPocDoc(opts);
  return savePocDoc({ ...opts, exists: !!existing, payload: opts.payload });
}

export { isBdApiEnabled, cacheGet, cacheSet };
